import React, { useState, useEffect } from 'react';
import { Button, Card, Col, Container, Form, Row, Alert } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { passwordResetRequest } from '../../../authConfig';
import { startEntraLoginRedirect } from '../../../utils/useEntraLoginRedirect';
import { logException, logInfo } from '../../../services/LoggingService';

//images - using dark logo like main app login
import logo from '../../../assets/img/logo-dark.png';
import logoBlank from '../../../assets/img/logo-blank.png'; // white, the old BioMADE logo's size, so the Telescope logo stays put

const ResetPassword = () => {
    const { instance } = useMsal();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [error, setError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    // Debug logging
    useEffect(() => {
        console.log('ResetPassword component mounted');
        return () => console.log('ResetPassword component unmounted');
    }, []);

    const handlePasswordReset = async (e) => {
        e.preventDefault();
        setError('');

        if (!email || !email.includes('@')) {
            setError('Please enter a valid email address');
            return;
        }

        setIsProcessing(true);

        try {
            // For Microsoft Entra External ID (CIAM), password reset is built into
            // the sign-up/sign-in user flow. We initiate a login redirect with login_hint
            // and the Microsoft sign-in page will display a "Forgot password?" link
            // that the user can click to start the password reset process.
            
            logInfo(`Password reset initiated for email: ${email}`, 'Password Reset');

            await startEntraLoginRedirect(instance, {
                ...passwordResetRequest,
                loginHint: email,
                prompt: 'login', // Force the login screen to appear
            });

        } catch (error) {
            console.error('Password reset error:', error);
            
            // Handle specific error codes for B2C/CIAM flows
            if (error.errorCode === 'AADB2C90118') {
                // User clicked "Forgot Password" - this is expected behavior
                // In a B2C flow, this would trigger the password reset policy
                setError('Password reset process initiated. Please check your email.');
            } else if (error.errorMessage && error.errorMessage.includes('AADB2C')) {
                // Other B2C-specific errors
                setError('Unable to initiate password reset. Please ensure self-service password reset is enabled in your Azure configuration.');
            } else {
                setError(
                    'Unable to initiate password reset. Please try again or contact support if the problem persists.'
                );
            }
            
            logException(error);
            setIsProcessing(false);
        }
    };

    return (
        <div className="hk-pg-wrapper pt-0 pb-xl-0 pb-5">
            <div className="hk-pg-body pt-0 pb-xl-0">
                <Container>
                    {/* Blank space where the BioMADE logo was */}
                    <img 
                        className="brand-img img-fluid mt-10" 
                        style={{ display: "block", maxWidth: "10%", height: "auto", margin: "0 auto" }} 
                        src={logoBlank} 
                        alt="" 
                    />
                    <img 
                        className="brand-img img-fluid" 
                        style={{ display: "block", maxWidth: "35%", height: "auto", margin: "0 auto", marginTop: "-60px" }} 
                        src={logo} 
                        alt="Telescope" 
                    />
                </Container>

                <Container>
                    <Row>
                        <Col xxl={5} xl={7} lg={10} className="mx-auto">
                            <Card className="text-center mt-4">
                                <Card.Body>
                                    <Card.Title>
                                        <h3>Reset your Password</h3>
                                    </Card.Title>
                                    <p className="mb-4">
                                        Enter your email address and we'll redirect you to complete your password reset
                                    </p>
                                    
                                    {error && (
                                        <Alert variant="danger" dismissible onClose={() => setError('')}>
                                            {error}
                                        </Alert>
                                    )}

                                    <Form onSubmit={handlePasswordReset}>
                                        <Form.Group className="mb-3">
                                            <Form.Label htmlFor="email">Email Address</Form.Label>
                                            <Form.Control 
                                                id="email"
                                                placeholder="Enter your email address" 
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                disabled={isProcessing}
                                                autoFocus
                                            />
                                        </Form.Group>
                                        
                                        <Button 
                                            variant="secondary" 
                                            className="btn-uppercase signInButton w-100 mb-3"
                                            type="submit"
                                            disabled={isProcessing}
                                        >
                                            {isProcessing ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                    Processing...
                                                </>
                                            ) : (
                                                'Continue to Password Reset'
                                            )}
                                        </Button>

                                        <div className="mt-3">
                                            <Link to="/" className="text-muted">
                                                <small>Back to Sign In</small>
                                            </Link>
                                        </div>
                                    </Form>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </div>
    )
}

export default ResetPassword
